import { RefreshCw } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import ConfirmationModal from "../../components/ConfirmationModal";
import ResponsiveActionButton from "../../components/ResponsiveActionButton";
import SplitScreenLayout from "../../components/SplitScreenLayout";
import { useTheme } from "../../contexts/ThemeContext";
import blobService from "../../services/blobService";
import {
  BucketContext,
  BucketType,
  ParsedFileInfo,
  ProviderBucketContext,
  RunPlutusBucketContext,
} from "../../types/blob";
import {
  BreadcrumbNav,
  BucketTabs,
  FileTable,
  FolderSelector,
  InlineFilePreview,
  ProviderSelector,
  UploadDropzone,
} from "./documents/components";

// Default folders for RunPlutus bucket
const DEFAULT_RUNPLUTUS_FOLDERS = ["Customer Agreement", "documents", "reports", "uploads"];

const DocumentManagerPage: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  // Tab state
  const [activeTab, setActiveTab] = useState<BucketType>("provider");

  // Provider tab state
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null
  );
  const [providerSubfolder, setProviderSubfolder] = useState<string>("");
  const [providerFolders, setProviderFolders] = useState<string[]>([]);

  // RunPlutus tab state
  const [runPlutusFolder, setRunPlutusFolder] = useState<string>("uploads");
  const [runPlutusFolders, setRunPlutusFolders] = useState<string[]>(DEFAULT_RUNPLUTUS_FOLDERS);

  // File list state
  const [files, setFiles] = useState<ParsedFileInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preview state for inline viewer
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<ParsedFileInfo | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Build context based on active tab - memoized to prevent unnecessary re-renders
  const context = useMemo((): BucketContext => {
    if (activeTab === "provider" && selectedProviderId) {
      return {
        bucketType: "provider",
        providerId: selectedProviderId,
        subfolder: providerSubfolder || undefined,
      } as ProviderBucketContext;
    }
    return {
      bucketType: "runplutus",
      folder: runPlutusFolder,
    } as RunPlutusBucketContext;
  }, [activeTab, selectedProviderId, providerSubfolder, runPlutusFolder]);

  // Extract unique folder names from file paths
  const extractFoldersFromFiles = (
    fileList: { name: string }[],
    basePath: string
  ): string[] => {
    const folders = new Set<string>();
    const basePrefix = basePath ? `${basePath}/` : "";

    fileList.forEach((file) => {
      // Remove base path prefix to get relative path
      let relativePath = file.name;
      if (basePrefix && relativePath.startsWith(basePrefix)) {
        relativePath = relativePath.slice(basePrefix.length);
      }

      // Get the first path segment (top-level folder at current level)
      const parts = relativePath.split("/");
      if (parts.length > 1) {
        // Has at least one folder level
        folders.add(parts[0]);
      }
    });

    return Array.from(folders).sort();
  };

  // Fetch provider folders (from root level)
  const fetchProviderFolders = useCallback(async () => {
    if (!selectedProviderId) {
      setProviderFolders([]);
      return;
    }

    try {
      // Fetch files at root level to discover folders
      const response = await blobService.listProviderFiles(selectedProviderId);
      const folders = extractFoldersFromFiles(response.files, selectedProviderId);
      setProviderFolders(folders);
    } catch (err) {
      console.error("Failed to fetch provider folders:", err);
    }
  }, [selectedProviderId]);

  // Fetch provider folders when provider changes
  useEffect(() => {
    fetchProviderFolders();
  }, [fetchProviderFolders]);

  // Fetch files
  const fetchFiles = useCallback(async () => {
    if (activeTab === "provider" && !selectedProviderId) {
      setFiles([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      let basePath: string;
      let stripPrefix: string | undefined;
      
      if (activeTab === "provider" && selectedProviderId) {
        response = await blobService.listProviderFiles(
          selectedProviderId,
          providerSubfolder || undefined
        );
        // Base path for provider is providerId + subfolder
        basePath = providerSubfolder 
          ? `${selectedProviderId}/${providerSubfolder}` 
          : selectedProviderId;
        // Strip provider ID from fileSubfolder since API already includes it in path
        stripPrefix = selectedProviderId;
      } else {
        response = await blobService.listRunPlutusFiles(runPlutusFolder);
        // Base path for RunPlutus is the folder
        basePath = runPlutusFolder;
        // No prefix to strip for RunPlutus - fileSubfolder should be full folder path
        stripPrefix = undefined;
      }

      // Parse files with virtual folder detection
      const parsedFiles = blobService.parseFilesWithFolders(response.files, basePath, stripPrefix);
      setFiles(parsedFiles);

      // Update folder list if we found new folders
      if (activeTab === "runplutus") {
        // Add current folder to list if not already there
        setRunPlutusFolders((prev) => {
          if (!prev.includes(runPlutusFolder)) {
            return [...prev, runPlutusFolder].sort();
          }
          return prev;
        });
      }
    } catch (err) {
      console.error("Failed to fetch files:", err);
      setError(err instanceof Error ? err.message : "Failed to load files");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedProviderId, providerSubfolder, runPlutusFolder]);

  // Fetch files when context changes
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Handle file upload
  const handleUpload = async (uploadedFiles: File[]) => {

    for (const file of uploadedFiles) {
      try {
        if (context.bucketType === "provider") {
          await blobService.uploadProviderFile(
            context.providerId,
            file.name,
            file,
            context.subfolder,
            false
          );
        } else {
          await blobService.uploadRunPlutusFile(
            context.folder,
            file.name,
            file,
            false
          );
        }
      } catch (err) {
        // Check if it's a file exists error
        const message =
          err instanceof Error ? err.message : "Upload failed";
        if (message.includes("already exists")) {
          // Could show overwrite modal here
          throw new Error(`File "${file.name}" already exists`);
        }
        throw err;
      }
    }

    // Refresh file list
    await fetchFiles();
  };

  // Handle folder navigation
  const handleNavigateToFolder = (folderName: string) => {
    if (activeTab === "provider") {
      const newPath = providerSubfolder
        ? `${providerSubfolder}/${folderName}`
        : folderName;
      setProviderSubfolder(newPath);
    } else {
      const newPath = `${runPlutusFolder}/${folderName}`;
      setRunPlutusFolder(newPath);
    }
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbNavigate = (path: string) => {
    if (activeTab === "provider") {
      setProviderSubfolder(path);
    } else {
      setRunPlutusFolder(path || "uploads");
    }
  };

  // Handle folder creation
  const handleCreateProviderFolder = (folderName: string) => {
    setProviderFolders((prev) => {
      if (!prev.includes(folderName)) {
        return [...prev, folderName].sort();
      }
      return prev;
    });
  };

  const handleCreateRunPlutusFolder = (folderName: string) => {
    setRunPlutusFolders((prev) => {
      if (!prev.includes(folderName)) {
        return [...prev, folderName].sort();
      }
      return prev;
    });
  };

  // Handle preview
  const previewableFiles = useMemo(
    () => files.filter((f) => f.isPreviewable),
    [files]
  );

  const handlePreview = (file: ParsedFileInfo) => {
    if (!file.isPreviewable) return;
    const index = previewableFiles.findIndex((f) => f.name === file.name);
    if (index === -1) return;
    setPreviewIndex(index);
  };

  // Handle preview navigation across previewable files only
  const handlePreviewNavigate = (index: number) => {
    if (index >= 0 && index < previewableFiles.length) {
      setPreviewIndex(index);
    }
  };

  // Handle download
  const handleDownload = async (file: ParsedFileInfo) => {
    try {
      let blob: Blob;

      if (context.bucketType === "provider") {
        blob = await blobService.downloadProviderFile(
          context.providerId,
          file.displayName,
          file.fileSubfolder || undefined
        );
      } else {
        // For RunPlutus, fileSubfolder contains the folder path
        const folder = file.fileSubfolder || context.folder;
        blob = await blobService.downloadRunPlutusFile(folder, file.displayName);
      }

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.displayName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  // Handle delete
  const handleDeleteClick = (file: ParsedFileInfo) => {
    setFileToDelete(file);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    setDeleting(true);
    try {

      if (context.bucketType === "provider") {
        await blobService.deleteProviderFile(
          context.providerId,
          fileToDelete.displayName,
          fileToDelete.fileSubfolder || undefined
        );
      } else {
        // For RunPlutus, fileSubfolder contains the folder path
        const folder = fileToDelete.fileSubfolder || context.folder;
        await blobService.deleteRunPlutusFile(folder, fileToDelete.displayName);
      }

      setDeleteModalOpen(false);
      setFileToDelete(null);
      await fetchFiles();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleting(false);
    }
  };

  // Handle copy URL
  const handleCopyUrl = async (file: ParsedFileInfo) => {
    try {
      let url: string;

      if (context.bucketType === "provider") {
        url = blobService.getProviderFileUrl(
          context.providerId,
          file.displayName,
          file.fileSubfolder || undefined
        );
      } else {
        // For RunPlutus, fileSubfolder contains the folder path
        const folder = file.fileSubfolder || context.folder;
        url = blobService.getRunPlutusFileUrl(folder, file.displayName);
      }

      await navigator.clipboard.writeText(url);
      // Could show toast here
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  const currentPath =
    activeTab === "provider" ? providerSubfolder : runPlutusFolder;

  // Left panel content - tabs, selectors, file table
  const leftPanelContent = (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 space-y-4 p-4">
        {/* Tab Navigation */}
        <BucketTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Context selectors */}
        <div
          className={`rounded-lg border p-4 ${
            isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"
          }`}
        >
          {activeTab === "provider" ? (
            <div className="flex flex-wrap gap-4">
              <div className="w-64">
                <ProviderSelector
                  selectedProviderId={selectedProviderId}
                  onProviderChange={(id) => {
                    setSelectedProviderId(id);
                    setProviderSubfolder("");
                  }}
                />
              </div>
              {selectedProviderId && (
                <div className="w-64">
                  <FolderSelector
                    folders={providerFolders}
                    selectedFolder={providerSubfolder}
                    onFolderSelect={setProviderSubfolder}
                    onCreateFolder={handleCreateProviderFolder}
                    loading={loading}
                    label="Subfolder"
                    placeholder="Root folder"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="w-64">
              <FolderSelector
                folders={runPlutusFolders}
                selectedFolder={runPlutusFolder}
                onFolderSelect={setRunPlutusFolder}
                onCreateFolder={handleCreateRunPlutusFolder}
                loading={loading}
                label="Folder"
                placeholder="Select a folder"
              />
            </div>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 min-h-0 overflow-hidden px-4 pb-4">
        {(activeTab === "provider" && selectedProviderId) ||
        activeTab === "runplutus" ? (
          <div
            className={`h-full flex flex-col rounded-lg border ${
              isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"
            }`}
          >
            {/* Breadcrumb + Upload area */}
            <div
              className={`flex-shrink-0 p-4 border-b ${
                isDarkMode ? "border-rp-separator" : "border-gray-200"
              }`}
            >
              <div className="mb-4">
                <BreadcrumbNav
                  currentPath={currentPath}
                  onNavigate={handleBreadcrumbNavigate}
                />
              </div>
              <UploadDropzone onUpload={handleUpload} disabled={loading} />
            </div>

            {/* Error state */}
            {error && (
              <div
                className={`flex-shrink-0 px-4 py-3 text-sm ${
                  isDarkMode
                    ? "bg-red-900/20 text-red-400"
                    : "bg-red-50 text-red-600"
                }`}
              >
                {error}
              </div>
            )}

            {/* File table - fills remaining space */}
            <div className="flex-1 min-h-0 overflow-auto">
              <FileTable
                files={files}
                context={context}
                loading={loading}
                onNavigateToFolder={handleNavigateToFolder}
                onPreview={handlePreview}
                onDownload={handleDownload}
                onDelete={handleDeleteClick}
                onCopyUrl={handleCopyUrl}
              />
            </div>
          </div>
        ) : (
          <div
            className={`rounded-lg border p-8 text-center ${
              isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"
            }`}
          >
            <p className={isDarkMode ? "text-rp-grey/50" : "text-gray-500"}>
              Select a provider to view and manage files
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <AdminPageLayout
      title="Document Manager"
      description="Manage files for providers and the RunPlutus platform"
      hideContentScroll
      actions={
        <ResponsiveActionButton
          label="Refresh"
          icon={<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />}
          onClick={fetchFiles}
          disabled={loading}
          variant="default"
        />
      }
    >
      <SplitScreenLayout
        sidebarPosition="right"
        isSidebarOpen={previewIndex !== null}
        rightSidebarWidth="50%"
        sidebarMinWidth={400}
        mainContentMinWidth={500}
        onCloseSidebar={() => setPreviewIndex(null)}
        sidebarContent={
          <InlineFilePreview
            files={files}
            currentIndex={previewIndex}
            context={context}
            onNavigate={handlePreviewNavigate}
            onClose={() => setPreviewIndex(null)}
            onDownload={handleDownload}
          />
        }
        mainContent={leftPanelContent}
      />
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setFileToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete File"
        message={`Are you sure you want to delete "${fileToDelete?.displayName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive
        loading={deleting}
      />
    </AdminPageLayout>
  );
};

export default DocumentManagerPage;

